import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import { EASE } from "@/components/Motion";

const EXPLORE_URL = process.env.REACT_APP_EXPLORE_URL;

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open;
  const tone = solid ? "text-[#0B2B22]" : "text-[#F4F7F4]";

  const linkClass = ({ isActive }) =>
    `rounded-full px-3.5 py-2 text-sm font-semibold tracking-wide transition-colors duration-300 ${tone} ${
      isActive
        ? "underline decoration-[#E05A47] decoration-2 underline-offset-8"
        : "opacity-75 hover:opacity-100"
    }`;

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid
          ? "border-b border-[#154234]/10 bg-white/85 shadow-[0_8px_30px_rgba(11,43,34,0.06)] backdrop-blur-xl"
          : "border-b border-white/10 bg-[#0B2B22]/45 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 sm:px-10 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <span
            className={`inline-flex items-center rounded-xl px-3 py-1.5 transition-all duration-500 ${
              solid ? "" : "bg-white/95 shadow-lg"
            }`}
          >
            <img src="/velopaths-logo.png" alt="VeloPaths" className="h-7 w-auto" />
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} data-testid={link.testid} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
          <a
            href={EXPLORE_URL}
            data-testid="nav-explore-cta"
            className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-[#E05A47] px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-colors duration-300 hover:bg-[#c94a39]"
          >
            Explore Trails <ArrowUpRight size={15} />
          </a>
          <Link
            to="/riders#waitlist"
            data-testid="nav-cta-get-app"
            className={`ml-2 inline-flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-sm font-bold backdrop-blur transition-colors duration-300 ${tone} ${
              solid
                ? "border-[#0B2B22]/30 hover:bg-[#0B2B22]/5"
                : "border-[#F4F7F4]/40 hover:bg-[#F4F7F4]/10"
            }`}
          >
            Get the App
          </Link>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          className={`grid size-10 place-items-center rounded-full ${tone} lg:hidden`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="overflow-hidden border-t border-[#154234]/10 bg-white/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              <motion.div
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                <a
                  href={EXPLORE_URL}
                  data-testid="nav-explore-cta-mobile"
                  className="mb-2 flex items-center justify-between rounded-xl bg-[#E05A47] px-4 py-3 font-display text-2xl font-bold tracking-tight text-white"
                >
                  Explore Trails <ArrowUpRight size={20} />
                </a>
              </motion.div>
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: EASE }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`${link.testid}-mobile`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 font-display text-2xl font-bold tracking-tight text-[#0B2B22] ${
                        isActive ? "bg-[#F4F7F4] text-[#2D6A4F]" : ""
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
